import { scheduledRunsApi } from 'apiConnector/scheduledRun/scheduledRuns';
import { SelectSearch } from 'components/shared/SelectSearch/SelectSearch';
import { useIsInternalUser } from 'hooks/useIsInternalUser';
import type { IBlotterQuoteItem } from 'models/IBlotterItem';
import { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { map } from 'rxjs';
import { EDITABLE_SCHEDULED_RUN_BLOTTER_ID, getEditableScheduledRun } from 'state/blotters/scheduledQuotes';
import { scheduledRunActions } from 'state/scheduledRuns/actions';
import type { AppState } from 'state/state.model';
import { httpClient } from 'utils/http/httpClient';
import { asSuccess } from 'utils/status';

const scheduleRunQuoteSelector = (state: AppState) => {
  const run = getEditableScheduledRun(state);
  return {
    excludedQuoteIds: run.quotes.map(({ quoteId }) => quoteId!),
    validateQuote: run.addQuote,
  };
};

const useAddQuote = () => {
  const dispatch = useDispatch();

  const addQuote = (quote: IBlotterQuoteItem) => {
    dispatch(
      scheduledRunActions.validateQuoteToAdd(EDITABLE_SCHEDULED_RUN_BLOTTER_ID, {
        quotes: [
          {
            quoteId: quote.externalQuoteId,
            productSubType: quote.initialInstrumentContractName,
            productType: quote.initialInstrumentName,
          },
        ],
      }),
    );
  };

  return addQuote;
};

const scheduledRunsClient = scheduledRunsApi(httpClient);

export function AddQuotePopup(props: Omit<ComponentProps<'div'>, 'children'>) {
  const { excludedQuoteIds = [], validateQuote } = useSelector(scheduleRunQuoteSelector);
  const addQuote = useAddQuote();

  return (
    <div {...props}>
      <div className="card">
        <div className="card-body p-3">
          <FormattedMessage id="scheduledRun.quotes.add.title" />
          <SelectSearch<IBlotterQuoteItem>
            autofocus
            placeholder="scheduledRun.quotes.add.placeholder"
            getSearchResult={txt => scheduledRunsClient.getQuotes(txt).pipe(map(res => res.entries))}
            onValueSelected={quote => quote && addQuote(quote)}
            resultRenderer={({ data, onSelected }) => (
              <div>
                <Result
                  quotes={data.filter(q => !excludedQuoteIds.includes(q.externalQuoteId))}
                  onSelected={onSelected}
                />
                {asSuccess(validateQuote)
                  ?.content?.results?.filter(({ isValid }) => !isValid)
                  .map(({ quoteId, productType, productSubType }) => (
                    <div key={quoteId} className="text-danger">
                      <FormattedMessage
                        id="scheduledRun.quotes.add.invalid"
                        values={{ productType, productSubType, quoteId }}
                      />
                    </div>
                  ))}
                <div></div>
              </div>
            )}
          ></SelectSearch>
        </div>
      </div>
    </div>
  );
}
interface ResultProps {
  quotes: IBlotterQuoteItem[];
  onSelected: (quote: IBlotterQuoteItem) => void;
}

function Result({ quotes, onSelected }: ResultProps) {
  const isInternal = useIsInternalUser();
  if (quotes.length === 0) {
    return;
  }
  return (
    <table className="table table-hover table-sm" data-e2e="quotes-search-items">
      <thead>
        <tr>
          <th>
            <FormattedMessage id="scheduledRun.quotes.result.columns.quoteId" />
          </th>
          {isInternal && (
            <th>
              <FormattedMessage id="scheduledRun.quotes.result.columns.pricerId" />
            </th>
          )}
          <th>
            <FormattedMessage id="scheduledRun.quotes.result.columns.payoff" />
          </th>
        </tr>
      </thead>
      <tbody>
        {quotes.map(q => (
          <tr key={q.externalQuoteId} onClick={() => onSelected(q)}>
            <td>{q.externalQuoteId}</td>
            {isInternal && <td>{q.pricerRfqId}</td>}
            <td>
              {q.productType} / {q.productSubType}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
